import {
  AuditDomain,
  MaturityLevel,
  DomainAssessment,
  AuditControl,
  AuditFinding,
  AuditDashboardStats,
  AuditTrendPoint,
  ComplianceSummary,
  AuditEngagement,
} from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// Mock Data: MoH Zambia Digital Systems Audit (WHO-Aligned v3)
// ─────────────────────────────────────────────────────────────────────────────

const mockDomainAssessments: DomainAssessment[] = [
  {
    domain: AuditDomain.GOVERNANCE_LEADERSHIP,
    currentMaturity: MaturityLevel.DEFINED,
    targetMaturity: MaturityLevel.MANAGED,
    score: 62,
    weightPercent: 15,
    controlsTotal: 12,
    controlsCompliant: 5,
    controlsPartial: 4,
    controlsNonCompliant: 3,
    findingsCount: 6,
    criticalFindings: 1,
    lastAssessedDate: '2026-01-15',
    assessor: 'Dr. M. Tembo',
    notes:
      'Digital health governance committee established but meeting irregularly. National Digital Health Strategy approved but implementation roadmap lacks milestones.',
  },
  {
    domain: AuditDomain.STRATEGY_INVESTMENT,
    currentMaturity: MaturityLevel.REPEATABLE,
    targetMaturity: MaturityLevel.DEFINED,
    score: 45,
    weightPercent: 12,
    controlsTotal: 10,
    controlsCompliant: 2,
    controlsPartial: 4,
    controlsNonCompliant: 4,
    findingsCount: 7,
    criticalFindings: 2,
    lastAssessedDate: '2026-01-15',
    assessor: 'Dr. M. Tembo',
    notes:
      'Digital health budget heavily dependent on donor funding (~78%). No dedicated line item in national health budget for digital health infrastructure.',
  },
  {
    domain: AuditDomain.LEGISLATION_POLICY_COMPLIANCE,
    currentMaturity: MaturityLevel.REPEATABLE,
    targetMaturity: MaturityLevel.MANAGED,
    score: 38,
    weightPercent: 15,
    controlsTotal: 14,
    controlsCompliant: 2,
    controlsPartial: 5,
    controlsNonCompliant: 7,
    findingsCount: 9,
    criticalFindings: 3,
    lastAssessedDate: '2026-01-20',
    assessor: 'L. Mwanza',
    notes:
      'Data Protection Act 2021 enacted but implementing regulations incomplete. No health-specific data governance framework. HIPAA/GDPR equivalence gap analysis not conducted.',
  },
  {
    domain: AuditDomain.WORKFORCE_CAPACITY,
    currentMaturity: MaturityLevel.REPEATABLE,
    targetMaturity: MaturityLevel.DEFINED,
    score: 42,
    weightPercent: 10,
    controlsTotal: 8,
    controlsCompliant: 1,
    controlsPartial: 4,
    controlsNonCompliant: 3,
    findingsCount: 5,
    criticalFindings: 1,
    lastAssessedDate: '2026-01-22',
    assessor: 'P. Banda',
    notes:
      'Limited digital health workforce. No structured training curriculum for health informatics. High turnover of trained IT staff at district level.',
  },
  {
    domain: AuditDomain.STANDARDS_INTEROPERABILITY,
    currentMaturity: MaturityLevel.REPEATABLE,
    targetMaturity: MaturityLevel.MANAGED,
    score: 40,
    weightPercent: 15,
    controlsTotal: 12,
    controlsCompliant: 2,
    controlsPartial: 5,
    controlsNonCompliant: 5,
    findingsCount: 8,
    criticalFindings: 2,
    lastAssessedDate: '2026-01-25',
    assessor: 'Dr. C. Phiri',
    notes:
      'HL7 FHIR adoption nascent (SmartCare Pro only). No national health data dictionary. Master Patient Index not yet operational. Limited use of ICD-11 coding.',
  },
  {
    domain: AuditDomain.INFRASTRUCTURE,
    currentMaturity: MaturityLevel.DEFINED,
    targetMaturity: MaturityLevel.MANAGED,
    score: 55,
    weightPercent: 10,
    controlsTotal: 10,
    controlsCompliant: 3,
    controlsPartial: 5,
    controlsNonCompliant: 2,
    findingsCount: 5,
    criticalFindings: 1,
    lastAssessedDate: '2026-01-28',
    assessor: 'K. Mulenga',
    notes:
      'National data centre operational but DR site incomplete. Cloud adoption policy drafted. ~65% of facilities with reliable connectivity. Power backup gaps at district hospitals.',
  },
  {
    domain: AuditDomain.SERVICES_APPLICATIONS,
    currentMaturity: MaturityLevel.DEFINED,
    targetMaturity: MaturityLevel.MANAGED,
    score: 58,
    weightPercent: 13,
    controlsTotal: 14,
    controlsCompliant: 5,
    controlsPartial: 6,
    controlsNonCompliant: 3,
    findingsCount: 7,
    criticalFindings: 1,
    lastAssessedDate: '2026-02-01',
    assessor: 'Dr. C. Phiri',
    notes:
      'Core systems (SmartCare, DHIS2, eLMIS) functional but fragmented. Multiple parallel EHR implementations. Application rationalisation needed. DHIS2 well-established for aggregate reporting.',
  },
  {
    domain: AuditDomain.SECURITY_PRIVACY,
    currentMaturity: MaturityLevel.AD_HOC,
    targetMaturity: MaturityLevel.DEFINED,
    score: 28,
    weightPercent: 10,
    controlsTotal: 14,
    controlsCompliant: 1,
    controlsPartial: 3,
    controlsNonCompliant: 10,
    findingsCount: 12,
    criticalFindings: 5,
    lastAssessedDate: '2026-02-05',
    assessor: 'K. Mulenga',
    notes:
      'Critical gaps: no CISO role, no incident response plan, minimal access controls on production databases, unencrypted data at rest in several systems, no regular penetration testing.',
  },
];

const mockControls: AuditControl[] = [
  // Governance & Leadership
  {
    id: 'ctrl-001',
    domain: AuditDomain.GOVERNANCE_LEADERSHIP,
    controlRef: 'GOV-01',
    title: 'Digital Health Governance Committee',
    description:
      'A multi-stakeholder governance body oversees digital health strategy, investment prioritisation, and standards adoption.',
    whoReference: 'WHO MAPS 3.1 — Leadership & Governance',
    complianceStatus: 'compliant',
    priority: 'critical',
    evidence: ['Committee ToR document', 'Meeting minutes Q1-Q4 2025'],
    gaps: [],
    recommendations: ['Increase meeting frequency to quarterly'],
    responsibleParty: 'Director of Health ICT',
    lastReviewDate: '2026-01-15',
  },
  {
    id: 'ctrl-002',
    domain: AuditDomain.GOVERNANCE_LEADERSHIP,
    controlRef: 'GOV-02',
    title: 'National Digital Health Strategy',
    description:
      'A costed, time-bound national digital health strategy aligned with the national health strategic plan and WHO recommendations.',
    whoReference:
      'WHO Global Strategy on Digital Health 2020-2025, Objective 1',
    complianceStatus: 'partial',
    priority: 'critical',
    evidence: ['National Digital Health Strategy 2023-2027 document'],
    gaps: [
      'Implementation roadmap lacks milestones and KPIs',
      'Costing incomplete for years 3-5',
    ],
    recommendations: [
      'Develop detailed implementation plan with M&E framework',
      'Complete costing exercise',
    ],
    responsibleParty: 'Director of Planning',
    lastReviewDate: '2026-01-15',
  },
  {
    id: 'ctrl-003',
    domain: AuditDomain.GOVERNANCE_LEADERSHIP,
    controlRef: 'GOV-03',
    title: 'Digital Health Architecture & Standards Body',
    description:
      'A technical body responsible for defining and enforcing enterprise architecture, data standards, and interoperability requirements.',
    whoReference: 'WHO MAPS 3.3 — Standards & Interoperability governance',
    complianceStatus: 'non-compliant',
    priority: 'high',
    evidence: [],
    gaps: [
      'No formal architecture review board',
      'No enterprise architecture documentation',
    ],
    recommendations: [
      'Establish Architecture Review Board',
      'Develop national digital health enterprise architecture',
    ],
    responsibleParty: 'Chief Health Informatics Officer',
    lastReviewDate: '2026-01-15',
  },
  // Strategy & Investment
  {
    id: 'ctrl-004',
    domain: AuditDomain.STRATEGY_INVESTMENT,
    controlRef: 'STR-01',
    title: 'Digital Health Budget Allocation',
    description:
      'Dedicated budget line for digital health in the national health budget, with sustainable domestic funding trajectory.',
    whoReference: 'WHO MAPS 3.2 — Strategy & Investment',
    complianceStatus: 'non-compliant',
    priority: 'critical',
    evidence: ['2025/26 National Health Budget'],
    gaps: ['No dedicated digital health budget line', '78% donor dependency'],
    recommendations: [
      'Advocate for dedicated budget line item',
      'Develop sustainability and transition plan',
    ],
    responsibleParty: 'Director of Finance',
    lastReviewDate: '2026-01-15',
    dueDate: '2026-06-30',
  },
  {
    id: 'ctrl-005',
    domain: AuditDomain.STRATEGY_INVESTMENT,
    controlRef: 'STR-02',
    title: 'Total Cost of Ownership Analysis',
    description:
      'TCO analysis conducted for all major digital health systems including hosting, licensing, maintenance, and human resource costs.',
    whoReference: 'WHO MAPS 3.2 — Investment planning',
    complianceStatus: 'non-compliant',
    priority: 'high',
    evidence: [],
    gaps: ['No TCO model exists', 'Hidden costs not tracked'],
    recommendations: [
      'Develop TCO model for top 10 systems',
      'Include in annual planning cycle',
    ],
    responsibleParty: 'Head of eHealth',
    lastReviewDate: '2026-01-15',
  },
  // Legislation, Policy & Compliance
  {
    id: 'ctrl-006',
    domain: AuditDomain.LEGISLATION_POLICY_COMPLIANCE,
    controlRef: 'LEG-01',
    title: 'Health Data Protection Regulations',
    description:
      'Health-specific data protection regulations implementing the Data Protection Act, including consent frameworks and data classification.',
    whoReference: 'WHO MAPS 3.3 — Policy, Legislation & compliance',
    complianceStatus: 'non-compliant',
    priority: 'critical',
    evidence: ['Data Protection Act 2021'],
    gaps: [
      'No health-specific implementing regulations',
      'No data classification policy',
      'No consent management framework',
    ],
    recommendations: [
      'Draft health data protection regulations',
      'Establish data classification scheme',
      'Implement consent management',
    ],
    responsibleParty: 'Legal Affairs',
    lastReviewDate: '2026-01-20',
    dueDate: '2026-09-30',
  },
  {
    id: 'ctrl-007',
    domain: AuditDomain.LEGISLATION_POLICY_COMPLIANCE,
    controlRef: 'LEG-02',
    title: 'Clinical Data Sharing Agreements',
    description:
      'Standardised data sharing agreements govern all inter-system and inter-organisational health data exchanges.',
    whoReference: 'WHO MAPS 3.3 — Data governance',
    complianceStatus: 'partial',
    priority: 'high',
    evidence: ['DHIS2 data sharing MOU'],
    gaps: [
      'Agreements not standardised',
      'Several systems share data without formal agreements',
    ],
    recommendations: [
      'Develop template data sharing agreement',
      'Audit all data flows and formalise agreements',
    ],
    responsibleParty: 'Chief Health Informatics Officer',
    lastReviewDate: '2026-01-20',
  },
  // Workforce & Capacity
  {
    id: 'ctrl-008',
    domain: AuditDomain.WORKFORCE_CAPACITY,
    controlRef: 'WRK-01',
    title: 'Digital Health Workforce Plan',
    description:
      'A workforce development plan addresses health informatics competencies, training, certification, and career pathways.',
    whoReference: 'WHO MAPS 3.4 — Workforce',
    complianceStatus: 'non-compliant',
    priority: 'high',
    evidence: [],
    gaps: [
      'No workforce plan',
      'No competency framework',
      'High turnover at district level',
    ],
    recommendations: [
      'Develop digital health workforce strategy',
      'Partner with universities for health informatics programmes',
    ],
    responsibleParty: 'HR Director',
    lastReviewDate: '2026-01-22',
    dueDate: '2026-12-31',
  },
  // Standards & Interoperability
  {
    id: 'ctrl-009',
    domain: AuditDomain.STANDARDS_INTEROPERABILITY,
    controlRef: 'SIO-01',
    title: 'National Health Data Dictionary',
    description:
      'A national health data dictionary standardises terminology, definitions, and coding across all health information systems.',
    whoReference: 'WHO MAPS 3.5 — Standards & Interoperability',
    complianceStatus: 'non-compliant',
    priority: 'critical',
    evidence: [],
    gaps: [
      'No national data dictionary',
      'Inconsistent coding across systems',
      'Limited ICD-11 adoption',
    ],
    recommendations: [
      'Develop national health data dictionary',
      'Mandate ICD-11 coding',
      'Align with SNOMED CT for clinical terms',
    ],
    responsibleParty: 'Chief Health Informatics Officer',
    lastReviewDate: '2026-01-25',
    dueDate: '2026-12-31',
  },
  {
    id: 'ctrl-010',
    domain: AuditDomain.STANDARDS_INTEROPERABILITY,
    controlRef: 'SIO-02',
    title: 'Health Information Exchange (HIE) Platform',
    description:
      'A national HIE enables standards-based data exchange between health systems using HL7 FHIR and/or OpenHIE architecture.',
    whoReference: 'WHO MAPS 3.5 — Interoperability; OpenHIE Architecture',
    complianceStatus: 'partial',
    priority: 'critical',
    evidence: [
      'OpenHIE architecture assessment report',
      'SmartCare Pro FHIR implementation',
    ],
    gaps: [
      'HIE not operational',
      'Only SmartCare Pro uses FHIR',
      'No shared health record',
    ],
    recommendations: [
      'Deploy national HIE using OpenHIM/OpenCR',
      'Mandate FHIR for all new systems',
      'Implement Master Patient Index',
    ],
    responsibleParty: 'Head of eHealth',
    lastReviewDate: '2026-01-25',
    dueDate: '2027-06-30',
  },
  // Infrastructure
  {
    id: 'ctrl-011',
    domain: AuditDomain.INFRASTRUCTURE,
    controlRef: 'INF-01',
    title: 'National Health Data Centre',
    description:
      'A Tier-3+ data centre hosts core health systems with redundancy, environmental controls, and 99.9% uptime SLA.',
    whoReference: 'WHO MAPS 3.6 — Infrastructure; ITU Data Centre Standards',
    complianceStatus: 'partial',
    priority: 'critical',
    evidence: ['Data centre audit report 2025', 'Uptime reports'],
    gaps: [
      'DR site not fully operational',
      'Environmental monitoring gaps',
      'Achieved 99.2% uptime (below target)',
    ],
    recommendations: [
      'Complete DR site commissioning',
      'Implement full environmental monitoring',
      'Upgrade cooling capacity',
    ],
    responsibleParty: 'Infrastructure Manager',
    lastReviewDate: '2026-01-28',
    dueDate: '2026-06-30',
  },
  {
    id: 'ctrl-012',
    domain: AuditDomain.INFRASTRUCTURE,
    controlRef: 'INF-02',
    title: 'Facility Connectivity',
    description:
      'Health facilities have reliable internet connectivity meeting minimum bandwidth requirements for digital health applications.',
    whoReference: 'WHO MAPS 3.6 — ICT Infrastructure',
    complianceStatus: 'partial',
    priority: 'high',
    evidence: ['Connectivity survey 2025'],
    gaps: [
      '~35% of facilities lack reliable connectivity',
      'Rural areas underserved',
      'No SLA with ISPs for health facilities',
    ],
    recommendations: [
      'Expand VSAT/fibre rollout',
      'Negotiate national health connectivity agreement',
      'Support offline-first application design',
    ],
    responsibleParty: 'Infrastructure Manager',
    lastReviewDate: '2026-01-28',
  },
  // Services & Applications
  {
    id: 'ctrl-013',
    domain: AuditDomain.SERVICES_APPLICATIONS,
    controlRef: 'SVC-01',
    title: 'Application Portfolio Rationalisation',
    description:
      'Digital health application portfolio is periodically reviewed, rationalised, and aligned to the national enterprise architecture.',
    whoReference: 'WHO MAPS 3.7 — Services & Applications',
    complianceStatus: 'non-compliant',
    priority: 'high',
    evidence: ['System inventory spreadsheet'],
    gaps: [
      '3 parallel EHR systems',
      'Duplicate functionality across systems',
      'No formal rationalisation process',
    ],
    recommendations: [
      'Conduct application portfolio review',
      'Develop migration/consolidation roadmap',
      'Define application lifecycle management policy',
    ],
    responsibleParty: 'Head of eHealth',
    lastReviewDate: '2026-02-01',
  },
  {
    id: 'ctrl-014',
    domain: AuditDomain.SERVICES_APPLICATIONS,
    controlRef: 'SVC-02',
    title: 'DHIS2 National HMIS',
    description:
      'DHIS2 is configured and maintained as the authoritative national HMIS platform with standardised indicators and reporting.',
    whoReference: 'WHO MAPS 3.7 — Health Management Information Systems',
    complianceStatus: 'compliant',
    priority: 'critical',
    evidence: [
      'DHIS2 configuration documentation',
      'Quarterly reporting compliance reports',
      'WHO indicator mapping',
    ],
    gaps: [],
    recommendations: [
      'Expand tracker programme coverage',
      'Integrate more facility-level systems via API',
    ],
    responsibleParty: 'HMIS Unit',
    lastReviewDate: '2026-02-01',
  },
  // Security & Privacy
  {
    id: 'ctrl-015',
    domain: AuditDomain.SECURITY_PRIVACY,
    controlRef: 'SEC-01',
    title: 'Chief Information Security Officer (CISO)',
    description:
      'A designated CISO or equivalent role is responsible for health information security strategy, policy, and incident management.',
    whoReference: 'WHO MAPS 3.8 — Security; ISO 27001',
    complianceStatus: 'non-compliant',
    priority: 'critical',
    evidence: [],
    gaps: [
      'No CISO role exists',
      'Security responsibilities fragmented',
      'No information security policy',
    ],
    recommendations: [
      'Appoint dedicated CISO',
      'Develop information security policy',
      'Establish security governance structure',
    ],
    responsibleParty: 'Permanent Secretary',
    lastReviewDate: '2026-02-05',
    dueDate: '2026-04-30',
  },
  {
    id: 'ctrl-016',
    domain: AuditDomain.SECURITY_PRIVACY,
    controlRef: 'SEC-02',
    title: 'Incident Response Plan',
    description:
      'A documented and tested cybersecurity incident response plan covers detection, containment, eradication, recovery, and lessons-learned.',
    whoReference: 'WHO MAPS 3.8 — Security; NIST CSF',
    complianceStatus: 'non-compliant',
    priority: 'critical',
    evidence: [],
    gaps: [
      'No incident response plan',
      'No security operations centre',
      'No breach notification procedure',
    ],
    recommendations: [
      'Develop and test incident response plan',
      'Conduct tabletop exercises',
      'Establish breach notification process',
    ],
    responsibleParty: 'Head of ICT',
    lastReviewDate: '2026-02-05',
    dueDate: '2026-06-30',
  },
  {
    id: 'ctrl-017',
    domain: AuditDomain.SECURITY_PRIVACY,
    controlRef: 'SEC-03',
    title: 'Data Encryption Standards',
    description:
      'All health data is encrypted at rest and in transit using current cryptographic standards (AES-256, TLS 1.2+).',
    whoReference: 'WHO MAPS 3.8 — Privacy; ISO 27001 A.10',
    complianceStatus: 'partial',
    priority: 'critical',
    evidence: ['TLS configuration report'],
    gaps: [
      'Several databases unencrypted at rest',
      'Legacy systems using TLS 1.0',
      'No encryption key management',
    ],
    recommendations: [
      'Enable encryption at rest for all databases',
      'Upgrade to TLS 1.2+',
      'Implement key management system',
    ],
    responsibleParty: 'Database Administrator',
    lastReviewDate: '2026-02-05',
    dueDate: '2026-06-30',
  },
  {
    id: 'ctrl-018',
    domain: AuditDomain.SECURITY_PRIVACY,
    controlRef: 'SEC-04',
    title: 'Access Control & Identity Management',
    description:
      'Role-based access controls, multi-factor authentication, and centralised identity management are implemented across all health systems.',
    whoReference: 'WHO MAPS 3.8 — Security; ISO 27001 A.9',
    complianceStatus: 'non-compliant',
    priority: 'critical',
    evidence: ['Access control audit sample'],
    gaps: [
      'No centralised identity management',
      'MFA not implemented',
      'Shared admin accounts on 4 systems',
      'No access review process',
    ],
    recommendations: [
      'Implement centralised identity provider (LDAP/SSO)',
      'Mandate MFA for all admin access',
      'Eliminate shared accounts',
      'Establish quarterly access reviews',
    ],
    responsibleParty: 'Head of ICT',
    lastReviewDate: '2026-02-05',
    dueDate: '2026-09-30',
  },
];

const mockFindings: AuditFinding[] = [
  {
    id: 'FND-001',
    domain: AuditDomain.SECURITY_PRIVACY,
    controlRef: 'SEC-04',
    title: 'Shared administrator accounts on production health systems',
    description:
      'Four production health information systems (SmartCare Legacy, eLMIS, DISA*Lab, LIMS) use shared administrator accounts, making it impossible to attribute actions to individual users and creating significant accountability and security risks.',
    severity: 'critical',
    status: 'open',
    category: 'non-conformity',
    affectedSystems: ['SmartCare Legacy', 'eLMIS', 'DISA*Lab', 'LIMS'],
    rootCause:
      'No centralised identity management; systems configured with default accounts during initial deployment.',
    recommendation:
      'Implement individual named accounts with role-based access. Deploy centralised identity provider. Disable shared accounts within 90 days.',
    remediationPlan:
      'Phase 1: Audit all admin accounts. Phase 2: Create individual accounts. Phase 3: Disable shared accounts. Phase 4: Implement SSO.',
    owner: 'Head of ICT',
    assignedTo: 'K. Mulenga',
    raisedDate: '2026-02-05',
    targetDate: '2026-05-31',
    evidence: [
      'Access control audit report',
      'System configuration screenshots',
    ],
    relatedFindings: ['FND-002'],
  },
  {
    id: 'FND-002',
    domain: AuditDomain.SECURITY_PRIVACY,
    controlRef: 'SEC-01',
    title: 'No designated Chief Information Security Officer',
    description:
      'The Ministry lacks a designated CISO or equivalent senior role with authority over information security. Security responsibilities are fragmented across multiple roles with no clear accountability.',
    severity: 'critical',
    status: 'open',
    category: 'non-conformity',
    affectedSystems: ['All MoH Digital Systems'],
    rootCause:
      'Role not established in organisational structure. Security seen as IT function rather than strategic priority.',
    recommendation:
      'Create and fill CISO position reporting to the Permanent Secretary. Develop information security governance structure.',
    owner: 'Permanent Secretary',
    assignedTo: 'HR Director',
    raisedDate: '2026-02-05',
    targetDate: '2026-04-30',
    evidence: ['Organisational chart', 'Job descriptions review'],
    relatedFindings: ['FND-001', 'FND-003'],
  },
  {
    id: 'FND-003',
    domain: AuditDomain.SECURITY_PRIVACY,
    controlRef: 'SEC-02',
    title: 'No cybersecurity incident response plan',
    description:
      'There is no documented incident response plan for cybersecurity events. Staff reported they would not know what to do in the event of a data breach or ransomware attack.',
    severity: 'critical',
    status: 'in-progress',
    category: 'non-conformity',
    affectedSystems: ['All MoH Digital Systems'],
    rootCause:
      'No CISO role; no security governance; limited cybersecurity awareness.',
    recommendation:
      'Develop incident response plan following NIST framework. Conduct tabletop exercises. Establish 24/7 security contact.',
    remediationPlan:
      'Draft IRP by March 2026. Review with stakeholders April 2026. Tabletop exercise May 2026. Go-live June 2026.',
    owner: 'Head of ICT',
    assignedTo: 'K. Mulenga',
    raisedDate: '2026-02-05',
    targetDate: '2026-06-30',
    evidence: ['Staff interview notes', 'Gap analysis report'],
    relatedFindings: ['FND-002'],
  },
  {
    id: 'FND-004',
    domain: AuditDomain.SECURITY_PRIVACY,
    controlRef: 'SEC-03',
    title: 'Unencrypted patient data at rest in production databases',
    description:
      'Patient health records in SmartCare Legacy and OpenMRS databases are stored without encryption at rest. Backup tapes are also unencrypted.',
    severity: 'critical',
    status: 'open',
    category: 'non-conformity',
    affectedSystems: ['SmartCare Legacy', 'OpenMRS'],
    rootCause:
      'Encryption not configured during deployment; no encryption policy existed.',
    recommendation:
      'Enable TDE for PostgreSQL and MySQL databases. Encrypt backup media. Implement key management.',
    owner: 'Database Administrator',
    assignedTo: 'Database Team',
    raisedDate: '2026-02-05',
    targetDate: '2026-06-30',
    evidence: ['Database configuration audit', 'Backup procedure review'],
    relatedFindings: [],
  },
  {
    id: 'FND-005',
    domain: AuditDomain.LEGISLATION_POLICY_COMPLIANCE,
    controlRef: 'LEG-01',
    title: 'No health-specific data protection regulations',
    description:
      'While the Data Protection Act 2021 provides general data protection, no health-specific implementing regulations exist. There is no defined consent framework for health data, no data classification scheme, and no guidance on cross-border health data transfers.',
    severity: 'critical',
    status: 'open',
    category: 'gap',
    affectedSystems: ['All health systems handling patient data'],
    rootCause:
      'Legislative gap; health sector regulations not yet drafted under Data Protection Act.',
    recommendation:
      'Draft health-specific data protection regulations. Establish health data classification scheme. Develop consent management framework.',
    owner: 'Legal Affairs',
    assignedTo: 'L. Mwanza',
    raisedDate: '2026-01-20',
    targetDate: '2026-09-30',
    evidence: ['Data Protection Act 2021', 'Legal gap analysis report'],
    relatedFindings: ['FND-004'],
  },
  {
    id: 'FND-006',
    domain: AuditDomain.STRATEGY_INVESTMENT,
    controlRef: 'STR-01',
    title: 'Unsustainable donor dependency for digital health funding',
    description:
      'Approximately 78% of digital health expenditure is funded by development partners (PEPFAR, Global Fund, GAVI, USAID). No transition plan or domestic resource mobilisation strategy exists.',
    severity: 'critical',
    status: 'open',
    category: 'gap',
    affectedSystems: ['All MoH Digital Systems'],
    rootCause:
      'No dedicated government budget line for digital health. Digital health not seen as priority in budget allocation.',
    recommendation:
      'Develop sustainability plan with transition milestones. Create dedicated digital health budget line. Explore innovative financing.',
    owner: 'Director of Finance',
    assignedTo: 'Budget Team',
    raisedDate: '2026-01-15',
    targetDate: '2026-06-30',
    evidence: ['Budget analysis 2023-2026', 'Donor mapping report'],
    relatedFindings: ['FND-007'],
  },
  {
    id: 'FND-007',
    domain: AuditDomain.STRATEGY_INVESTMENT,
    controlRef: 'STR-02',
    title: 'No Total Cost of Ownership analysis for digital health systems',
    description:
      'TCO is not tracked for any of the 15+ digital health systems. Hidden costs (staff time, training, support) not captured. Makes informed investment decisions impossible.',
    severity: 'high',
    status: 'open',
    category: 'gap',
    affectedSystems: ['All MoH Digital Systems'],
    rootCause: 'No financial management framework for digital health assets.',
    recommendation:
      'Develop TCO template. Calculate TCO for top 10 systems. Integrate into annual planning.',
    owner: 'Head of eHealth',
    assignedTo: 'Finance Team',
    raisedDate: '2026-01-15',
    targetDate: '2026-09-30',
    evidence: [],
    relatedFindings: ['FND-006'],
  },
  {
    id: 'FND-008',
    domain: AuditDomain.STANDARDS_INTEROPERABILITY,
    controlRef: 'SIO-01',
    title: 'No national health data dictionary',
    description:
      'Health systems use inconsistent terminology, variable names, and coding schemes. No master data dictionary exists to standardise definitions across the digital health ecosystem.',
    severity: 'high',
    status: 'open',
    category: 'gap',
    affectedSystems: ['SmartCare Pro', 'DHIS2', 'eLMIS', 'OpenMRS'],
    rootCause:
      'No standards body; no enterprise architecture function; systems developed independently by different partners.',
    recommendation:
      'Establish national health data dictionary. Mandate use by all systems. Align with WHO ICD-11 and SNOMED CT.',
    owner: 'Chief Health Informatics Officer',
    assignedTo: 'Standards Team',
    raisedDate: '2026-01-25',
    targetDate: '2026-12-31',
    evidence: ['Data element comparison matrix'],
    relatedFindings: ['FND-009'],
  },
  {
    id: 'FND-009',
    domain: AuditDomain.STANDARDS_INTEROPERABILITY,
    controlRef: 'SIO-02',
    title: 'Health Information Exchange not operational',
    description:
      'While OpenHIE architecture has been assessed, no HIE platform is operational. Systems exchange data via point-to-point CSV/Excel uploads rather than standards-based APIs.',
    severity: 'high',
    status: 'in-progress',
    category: 'gap',
    affectedSystems: ['All systems requiring data exchange'],
    rootCause:
      'No HIE platform deployed. Limited FHIR adoption. No interoperability mandate.',
    recommendation:
      'Deploy OpenHIM/OpenCR-based HIE. Mandate FHIR for all new systems. Implement MPI.',
    remediationPlan:
      'Phase 1: Deploy OpenHIM (Q2 2026). Phase 2: Connect DHIS2 + SmartCare Pro (Q3 2026). Phase 3: MPI rollout (Q4 2026).',
    owner: 'Head of eHealth',
    assignedTo: 'Interoperability Team',
    raisedDate: '2026-01-25',
    targetDate: '2027-06-30',
    evidence: ['OpenHIE assessment report', 'Data flow mapping'],
    relatedFindings: ['FND-008'],
  },
  {
    id: 'FND-010',
    domain: AuditDomain.SERVICES_APPLICATIONS,
    controlRef: 'SVC-01',
    title: 'Multiple parallel EHR systems with overlapping functionality',
    description:
      'Three EHR systems (SmartCare Legacy, SmartCare Plus, SmartCare Pro) are concurrently deployed, along with OpenMRS at some facilities. This creates data fragmentation, duplicated training, and maintenance burden.',
    severity: 'high',
    status: 'in-progress',
    category: 'observation',
    affectedSystems: [
      'SmartCare Legacy',
      'SmartCare Plus',
      'SmartCare Pro',
      'OpenMRS',
    ],
    rootCause:
      'Evolutionary deployment without formal rationalisation; different donor-funded programmes deploying different systems.',
    recommendation:
      'Develop application rationalisation roadmap. Define migration path from legacy systems. Establish application lifecycle policy.',
    remediationPlan:
      'SmartCare Legacy EOL by Dec 2026. SmartCare Plus migration to Pro by Dec 2027.',
    owner: 'Head of eHealth',
    assignedTo: 'Dr. C. Phiri',
    raisedDate: '2026-02-01',
    targetDate: '2027-12-31',
    evidence: ['System inventory', 'Facility deployment map'],
    relatedFindings: [],
  },
  {
    id: 'FND-011',
    domain: AuditDomain.WORKFORCE_CAPACITY,
    controlRef: 'WRK-01',
    title: 'High turnover of trained IT staff at district level',
    description:
      'District-level IT officers have an average tenure of 14 months. Training investment lost when staff leave. No knowledge management or handover processes.',
    severity: 'high',
    status: 'open',
    category: 'observation',
    affectedSystems: ['All district-deployed systems'],
    rootCause:
      'Non-competitive salaries; limited career progression; rural posting challenges.',
    recommendation:
      'Develop retention incentives. Create digital health career pathway. Implement knowledge management practices.',
    owner: 'HR Director',
    assignedTo: 'P. Banda',
    raisedDate: '2026-01-22',
    targetDate: '2026-12-31',
    evidence: ['Staff turnover data 2023-2025', 'Exit interview summary'],
    relatedFindings: [],
  },
  {
    id: 'FND-012',
    domain: AuditDomain.INFRASTRUCTURE,
    controlRef: 'INF-01',
    title: 'Disaster recovery site not fully operational',
    description:
      'While identified and partially equipped, the DR site cannot fully assume national data centre operations within the target RTO of 4 hours.',
    severity: 'high',
    status: 'in-progress',
    category: 'gap',
    affectedSystems: ['National Data Centre', 'All hosted systems'],
    rootCause:
      'Funding constraints; incomplete equipment procurement; replication not fully configured.',
    recommendation:
      'Complete DR site provisioning. Configure real-time replication. Conduct DR test.',
    remediationPlan:
      'Equipment procurement Q1 2026. Configuration Q2 2026. Full DR test Q3 2026.',
    owner: 'Infrastructure Manager',
    assignedTo: 'DC Operations Team',
    raisedDate: '2026-01-28',
    targetDate: '2026-09-30',
    evidence: ['DR site assessment', 'BCP document draft'],
    relatedFindings: [],
  },
  {
    id: 'FND-013',
    domain: AuditDomain.GOVERNANCE_LEADERSHIP,
    controlRef: 'GOV-02',
    title: 'Digital health strategy implementation lacks M&E framework',
    description:
      'National Digital Health Strategy 2023-2027 approved but no monitoring and evaluation framework tracks implementation progress. No KPIs defined.',
    severity: 'medium',
    status: 'open',
    category: 'observation',
    affectedSystems: [],
    rootCause:
      'Strategy developed without M&E component; no dedicated M&E resources for digital health.',
    recommendation:
      'Develop M&E framework with indicators aligned to WHO MAPS toolkit. Report progress quarterly.',
    owner: 'Director of Planning',
    assignedTo: 'M&E Team',
    raisedDate: '2026-01-15',
    targetDate: '2026-06-30',
    evidence: ['National Digital Health Strategy document'],
    relatedFindings: [],
  },
  {
    id: 'FND-014',
    domain: AuditDomain.SERVICES_APPLICATIONS,
    controlRef: 'SVC-02',
    title: 'DHIS2 tracker programmes underutilised',
    description:
      'DHIS2 is well-established for aggregate reporting but tracker programmes for individual-level data are only deployed in 3 of 10 provinces.',
    severity: 'medium',
    status: 'in-progress',
    category: 'opportunity-for-improvement',
    affectedSystems: ['DHIS2'],
    rootCause:
      'Limited training on tracker; connectivity constraints at facility level; competing EHR systems.',
    recommendation:
      'Expand tracker programmes nationally. Integrate with SmartCare Pro for automatic reporting.',
    owner: 'HMIS Unit',
    assignedTo: 'DHIS2 Team',
    raisedDate: '2026-02-01',
    targetDate: '2027-03-31',
    evidence: ['DHIS2 tracker coverage report', 'Province deployment status'],
    relatedFindings: ['FND-010'],
  },
  {
    id: 'FND-015',
    domain: AuditDomain.SECURITY_PRIVACY,
    controlRef: 'SEC-03',
    title: 'Legacy systems using deprecated TLS versions',
    description:
      'SmartCare Legacy and DISA*Lab web interfaces are configured with TLS 1.0/1.1, which are deprecated and vulnerable to known attacks.',
    severity: 'high',
    status: 'open',
    category: 'non-conformity',
    affectedSystems: ['SmartCare Legacy', 'DISA*Lab'],
    rootCause:
      'Legacy system configurations not updated; no security patching schedule.',
    recommendation:
      'Upgrade to TLS 1.2+ immediately. Implement automated security patching.',
    owner: 'Head of ICT',
    assignedTo: 'Systems Team',
    raisedDate: '2026-02-05',
    targetDate: '2026-03-31',
    evidence: ['SSL/TLS scan results'],
    relatedFindings: ['FND-004'],
  },
];

const mockTrendData: AuditTrendPoint[] = [
  {
    period: 'Q1 2025',
    overallScore: 35,
    maturityLevel: MaturityLevel.AD_HOC,
    findingsOpen: 18,
    findingsClosed: 2,
  },
  {
    period: 'Q2 2025',
    overallScore: 39,
    maturityLevel: MaturityLevel.REPEATABLE,
    findingsOpen: 20,
    findingsClosed: 5,
  },
  {
    period: 'Q3 2025',
    overallScore: 42,
    maturityLevel: MaturityLevel.REPEATABLE,
    findingsOpen: 17,
    findingsClosed: 8,
  },
  {
    period: 'Q4 2025',
    overallScore: 45,
    maturityLevel: MaturityLevel.REPEATABLE,
    findingsOpen: 15,
    findingsClosed: 12,
  },
  {
    period: 'Q1 2026',
    overallScore: 46,
    maturityLevel: MaturityLevel.REPEATABLE,
    findingsOpen: 15,
    findingsClosed: 14,
  },
];

const mockEngagement: AuditEngagement = {
  id: 'AUD-2026-001',
  title: 'MoH Digital Systems Audit — WHO-Aligned Assessment v3',
  auditType: 'full',
  status: 'review',
  scope:
    'All MoH digital health systems, infrastructure, governance frameworks, and supporting processes across all 10 provinces.',
  objectives: [
    'Assess digital health maturity across 8 WHO-aligned domains',
    'Identify critical gaps in security, interoperability, and governance',
    'Evaluate compliance with Data Protection Act 2021',
    'Assess sustainability of digital health investments',
    'Provide actionable recommendations aligned with WHO Global Strategy on Digital Health',
  ],
  framework: 'WHO Digital Health Assessment Framework v3 (MAPS-aligned)',
  leadAuditor: 'Dr. M. Tembo',
  auditTeam: [
    'Dr. M. Tembo',
    'L. Mwanza',
    'P. Banda',
    'Dr. C. Phiri',
    'K. Mulenga',
  ],
  startDate: '2026-01-10',
  endDate: '2026-02-10',
  reportDate: '2026-02-28',
  domainAssessments: mockDomainAssessments,
  findings: mockFindings,
  overallScore: 46,
  overallMaturity: MaturityLevel.REPEATABLE,
};

// ─────────────────────────────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────────────────────────────

export class AuditService {
  async getDomainAssessments(): Promise<DomainAssessment[]> {
    return Promise.resolve(mockDomainAssessments);
  }

  async getDomainAssessment(
    domain: AuditDomain,
  ): Promise<DomainAssessment | undefined> {
    return Promise.resolve(
      mockDomainAssessments.find(a => a.domain === domain),
    );
  }

  async getControls(): Promise<AuditControl[]> {
    return Promise.resolve(mockControls);
  }

  async getControlsByDomain(domain: AuditDomain): Promise<AuditControl[]> {
    return Promise.resolve(mockControls.filter(c => c.domain === domain));
  }

  async getFindings(): Promise<AuditFinding[]> {
    return Promise.resolve(mockFindings);
  }

  async getFindingsByDomain(domain: AuditDomain): Promise<AuditFinding[]> {
    return Promise.resolve(mockFindings.filter(f => f.domain === domain));
  }

  async getFinding(id: string): Promise<AuditFinding | undefined> {
    return Promise.resolve(mockFindings.find(f => f.id === id));
  }

  async getDashboardStats(): Promise<AuditDashboardStats> {
    const controls = mockControls;
    const findings = mockFindings;
    const now = new Date();

    return Promise.resolve({
      overallScore: mockEngagement.overallScore,
      overallMaturity: mockEngagement.overallMaturity,
      totalControls: controls.length,
      compliantControls: controls.filter(
        c => c.complianceStatus === 'compliant',
      ).length,
      partialControls: controls.filter(c => c.complianceStatus === 'partial')
        .length,
      nonCompliantControls: controls.filter(
        c => c.complianceStatus === 'non-compliant',
      ).length,
      notAssessedControls: controls.filter(
        c => c.complianceStatus === 'not-assessed',
      ).length,
      totalFindings: findings.length,
      openFindings: findings.filter(f => f.status === 'open').length,
      criticalFindings: findings.filter(f => f.severity === 'critical').length,
      highFindings: findings.filter(f => f.severity === 'high').length,
      remediatedFindings: findings.filter(
        f => f.status === 'remediated' || f.status === 'closed',
      ).length,
      overdueFindings: findings.filter(
        f => f.status === 'open' && new Date(f.targetDate) < now,
      ).length,
      domainsAssessed: mockDomainAssessments.length,
      totalDomains: 8,
      lastAuditDate: mockEngagement.endDate,
      nextAuditDate: '2026-07-01',
      trendData: mockTrendData,
    });
  }

  async getComplianceSummary(): Promise<ComplianceSummary[]> {
    const domains = Object.values(AuditDomain);
    return Promise.resolve(
      domains.map(domain => {
        const domainControls = mockControls.filter(c => c.domain === domain);
        const total = domainControls.length;
        const compliant = domainControls.filter(
          c => c.complianceStatus === 'compliant',
        ).length;
        const partial = domainControls.filter(
          c => c.complianceStatus === 'partial',
        ).length;
        const nonCompliant = domainControls.filter(
          c => c.complianceStatus === 'non-compliant',
        ).length;
        const notAssessed = domainControls.filter(
          c => c.complianceStatus === 'not-assessed',
        ).length;
        return {
          domain,
          totalControls: total,
          compliant,
          partial,
          nonCompliant,
          notAssessed,
          compliancePercent:
            total > 0
              ? Math.round(((compliant + partial * 0.5) / total) * 100)
              : 0,
        };
      }),
    );
  }

  async getEngagement(): Promise<AuditEngagement> {
    return Promise.resolve(mockEngagement);
  }

  async getTrendData(): Promise<AuditTrendPoint[]> {
    return Promise.resolve(mockTrendData);
  }
}

export const auditService = new AuditService();
